'use client'

import { cn } from '@brew/ui/cn'
import { MOTION_DURATION, MOTION_EASE } from '@brew/ui/motion'
import { ArrowUpRight, Clock } from 'lucide-react'
import { motion, useInView } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import {
  MK_BODY,
  MK_CAPTION,
  MK_CARD_TITLE,
  MK_SECTION_TITLE,
  MK_SMALL,
} from '@/components/marketing/home/home-layout'
import { MARKETING_CANVAS_BG } from '@/components/marketing/v2/marketing-layout'
import { IntentPrefetchLink } from '@/components/navigation/intent-prefetch-link'
import { getAuthorByName } from '@/lib/site/authors'
import { categories, type Post } from '@/lib/site/blog-types'

type BlogPost = Post & { readingTime?: number }

/**
 * Strip inline `[text](url)` markdown link syntax from a summary string for
 * the timeline preview. The full post page renders links inline via the
 * `renderInlineLinks` helper; the timeline shows plain text only because we
 * don't want underlined hyperlinks fighting for attention with the card
 * itself (which is already the click target).
 */
function summaryPlainText(text: string): string {
  return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
}

interface BlogTimelineProps {
  posts: Array<BlogPost>
  selectedCategory?: string
}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * Blog frontmatter stores calendar dates as `YYYY-MM-DD`. JavaScript parses
 * that shape as UTC midnight, so reading it back with local-time getters can
 * move the post to the prior day in timezones west of UTC. Keep every calendar
 * operation in UTC so server rendering and browser hydration agree.
 */
function parsePublishedDate(publishedAt: string): Date {
  return new Date(
    DATE_ONLY_PATTERN.test(publishedAt)
      ? `${publishedAt}T00:00:00.000Z`
      : publishedAt
  )
}

// Group posts by month
function groupPostsByMonth(posts: Array<BlogPost>) {
  const groups: {
    [key: string]: { monthLabel: string; posts: Array<BlogPost> }
  } = {}

  posts.forEach((post) => {
    const date = parsePublishedDate(post.publishedAt)
    const monthKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
    const monthLabel = date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    })

    if (!groups[monthKey]) {
      groups[monthKey] = { monthLabel, posts: [] }
    }
    groups[monthKey].posts.push(post)
  })

  // Sort groups by date (newest first)
  return Object.entries(groups)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, value]) => ({
      monthKey: key,
      monthLabel: value.monthLabel,
      posts: value.posts.sort(
        (a, b) =>
          parsePublishedDate(b.publishedAt).getTime() -
          parsePublishedDate(a.publishedAt).getTime()
      ),
    }))
}

export function BlogTimeline({ posts, selectedCategory }: BlogTimelineProps) {
  const [visibleMonth, setVisibleMonth] = useState('')
  const groupedPosts = groupPostsByMonth(posts)
  const currentMonth = groupedPosts.some(
    (group) => group.monthLabel === visibleMonth
  )
    ? visibleMonth
    : (groupedPosts[0]?.monthLabel ?? '')
  const [currentMonthName = '', currentMonthYear = ''] = currentMonth.split(' ')

  if (posts.length === 0) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg text-muted-foreground">
          No articles found{selectedCategory ? ' in this category' : ''}.
        </p>
        {selectedCategory ? (
          <Link
            href="/blog"
            className="mt-2 inline-block text-primary hover:underline"
          >
            View all articles →
          </Link>
        ) : null}
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Sticky Month Header */}
      <header
        className={cn(
          'sticky top-16 z-20 backdrop-blur-sm',
          MARKETING_CANVAS_BG
        )}
      >
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-between py-4">
            <motion.div
              key={currentMonth}
              initial={{ y: -8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="flex items-baseline gap-3"
            >
              <span className={cn(MK_SECTION_TITLE, 'text-mk-ink')}>
                {currentMonthName}
              </span>
              <span className={cn(MK_BODY, 'text-mk-body')}>
                {currentMonthYear}
              </span>
            </motion.div>
            <div className={cn(MK_SMALL, 'text-mk-body tabular-nums')}>
              {posts.length} article{posts.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </header>

      {/* Timeline Content */}
      <div className="mx-auto max-w-5xl pb-12">
        {groupedPosts.map((group, groupIndex) => (
          <MonthGroup
            key={group.monthKey}
            group={group}
            setCurrentMonth={setVisibleMonth}
            isFirst={groupIndex === 0}
          />
        ))}
      </div>
    </div>
  )
}

function MonthGroup({
  group,
  setCurrentMonth,
  isFirst,
}: {
  group: { monthKey: string; monthLabel: string; posts: Array<BlogPost> }
  setCurrentMonth: (month: string) => void
  isFirst: boolean
}) {
  const groupRef = useRef(null)

  const isInView = useInView(groupRef, {
    amount: 0,
    margin: '-80px 0px -90% 0px',
  })

  useEffect(() => {
    if (isInView) {
      setCurrentMonth(group.monthLabel)
    }
  }, [isInView, group.monthLabel, setCurrentMonth])

  return (
    <div ref={groupRef} className={isFirst ? 'pt-8' : 'pt-16'}>
      {/* Month Divider (for non-first groups) */}
      {!isFirst && (
        <div className="mb-8 flex items-center gap-4">
          <div className="h-px flex-1 bg-mk-line" />
          <span
            className={cn(
              MK_CAPTION,
              'rounded-full bg-mk-tile px-3 py-1.5 font-medium text-mk-body shadow-[0_0_0_1px_var(--mk-ring)]'
            )}
          >
            {group.monthLabel}
          </span>
          <div className="h-px flex-1 bg-mk-line" />
        </div>
      )}

      {/* Posts Grid */}
      <div className="flex flex-col gap-6">
        {group.posts.map((post, index) => (
          <BlogTimelineCard
            key={post.slug}
            post={post}
            isAboveFold={isFirst && index < ABOVE_FOLD_CARDS}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * The cards a visitor sees without scrolling. They render in place (no
 * fade-in waiting on hydration) and preload their image, because the first
 * card's image is the page's largest paint on a phone.
 */
const ABOVE_FOLD_CARDS = 2

function BlogTimelineCard({
  post,
  isAboveFold,
}: {
  post: BlogPost
  isAboveFold: boolean
}) {
  const cardRef = useRef(null)
  const isInView = useInView(cardRef, { once: true, margin: '-50px' })
  const author = getAuthorByName(post.author)
  const category = post.category ? categories[post.category] : null
  const publishDate = parsePublishedDate(post.publishedAt)

  const dayOfMonth = publishDate.getUTCDate()
  const dayOfWeek = publishDate.toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  })

  return (
    <motion.div
      ref={cardRef}
      initial={isAboveFold ? false : { opacity: 0, y: 12 }}
      animate={
        isAboveFold || isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }
      }
      // Each card reveals the moment it scrolls in, so there is no stagger:
      // an index-based delay only made the card the visitor is looking at
      // wait (the index resets per month, so it never sequenced anything).
      transition={{
        duration: MOTION_DURATION.slow,
        ease: MOTION_EASE.smoothOut,
      }}
    >
      <IntentPrefetchLink href={`/blog/${post.slug}`} className="group block">
        <article className="relative -mx-4 grid grid-cols-1 gap-6 rounded-3xl p-4 transition-colors duration-quick hover:bg-mk-tile md:-mx-6 md:grid-cols-[auto_1fr] md:gap-8 md:p-6">
          {/* Date Column (Desktop) */}
          <div className="hidden w-16 flex-col items-center justify-start pt-1 md:flex">
            <span className={cn(MK_CARD_TITLE, 'text-mk-ink tabular-nums')}>
              {dayOfMonth}
            </span>
            <span className={cn(MK_CAPTION, 'text-mk-body uppercase')}>
              {dayOfWeek}
            </span>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-5 md:flex-row-reverse md:gap-6">
            {/* Image */}
            <div className="relative aspect-[1200/630] w-full flex-shrink-0 overflow-hidden rounded-2xl bg-mk-tile shadow-[0_0_0_1px_var(--mk-ring)] md:w-72 lg:w-96">
              {post.image ? (
                <Image
                  className="object-contain transition-transform duration-500 group-hover:scale-105"
                  src={post.image}
                  fill
                  sizes="(max-width: 768px) 100vw, 400px"
                  alt={post.title}
                  quality={70}
                  preload={isAboveFold}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                  <span className="text-muted-foreground text-sm">
                    No image
                  </span>
                </div>
              )}

              {/* Category Badge */}
              {category ? (
                <div className="absolute top-3 right-3">
                  <span className="flex items-center rounded-lg bg-primary px-2.5 py-1 font-medium text-primary-foreground text-xs shadow-sm">
                    {category.label}
                  </span>
                </div>
              ) : null}
            </div>

            {/* Text Content */}
            <div className="flex min-w-0 flex-1 flex-col">
              {/* Mobile Date + Meta */}
              <div
                className={cn(
                  MK_SMALL,
                  'mb-3 flex items-center gap-3 text-mk-body'
                )}
              >
                <span className="font-medium md:hidden">
                  {publishDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                  })}
                </span>
                {post.readingTime ? (
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {post.readingTime} min read
                  </span>
                ) : null}
              </div>

              {/* Title */}
              <h2
                className={cn(
                  MK_CARD_TITLE,
                  'mb-3 line-clamp-2 text-mk-ink transition-colors group-hover:text-mk-accent'
                )}
              >
                {post.title}
                <ArrowUpRight className="ml-2 inline-block h-5 w-5 translate-x-0 -translate-y-0.5 opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:opacity-70" />
              </h2>

              {/* Summary */}
              <p
                className={cn(MK_BODY, 'mb-4 line-clamp-2 flex-1 text-mk-body')}
              >
                {summaryPlainText(post.summary)}
              </p>

              {/* Author */}
              {author && (
                <div className="flex items-center gap-3 border-mk-line border-t pt-3">
                  <Image
                    src={author.avatar}
                    alt={author.name}
                    width={32}
                    height={32}
                    className="rounded-full object-cover ring-2 ring-background"
                  />
                  <div className="flex flex-col">
                    <span className={cn(MK_SMALL, 'font-medium text-mk-ink')}>
                      {author.name}
                    </span>
                    <span className={cn(MK_CAPTION, 'text-mk-body')}>
                      {author.role}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </IntentPrefetchLink>
    </motion.div>
  )
}
